import React from 'react'
import { useNavigate } from 'react-router-dom'

import { Stack, Typography } from '@mui/material'

import { Alert, Button, Modal } from '@repo/ui'
import type { SeatUsage } from '@repo/types'

import { PermissionGate } from 'components/access'
import { useReferrerState } from 'hooks'
import { ROUTES } from 'routes'

interface SeatLimitModalProps {
  open: boolean
  onClose: () => void
  /**
   * The company's current usage. Null renders nothing — the modal exists to name
   * a specific limit, and "your plan is full" without the number is the vague
   * message this component was built to avoid.
   */
  seats: SeatUsage | null
}

/**
 * Shown instead of the invite modal once a company is at its plan's seat limit — VRG-438.
 *
 * NOT a destructive confirm. Nothing is about to be lost, so nothing here is red: this
 * explains why an action did not happen and offers the one thing that resolves it. The
 * shared `ConfirmSettingModal` was the obvious reuse and is the wrong shape — its
 * `consequence` always renders as a warning `Alert` (§43), which is exactly the tone this
 * must not take.
 *
 * ⚠️ This is a DELIBERATE deviation from working-conventions §46, which says a record-state
 * block HIDES the action. §46's own reasoning is that a tombstone does not help and the app
 * owes the user the RESOLVING action instead — and here the blocked click IS how the
 * resolving action is reached. Hiding Invite at the limit would leave a Users page with no
 * explanation of why people can no longer be added and no route to fixing it, which is the
 * dead end §46 exists to prevent. Do not "fix" this by hiding the button.
 */
export const SeatLimitModal = ({ open, onClose, seats }: SeatLimitModalProps) => {
  const navigate = useNavigate()
  /**
   * §42 — derived HERE rather than taken as a prop from whichever page opened the modal.
   *
   * A prop would be the opt-in this rule exists to kill: the next surface to open this
   * modal gets no back-link and nothing reports it, which is the 2-of-37 failure §42
   * documents for the old `backLabel` prop. The hook reads the CURRENT location, so it
   * is correct for any caller by construction. Opened from /settings/users it derives
   * `backLabel: "Users"` — verified in the browser, not assumed, since the derivation
   * walks to the nearest segment that is not a record id or a routing prefix.
   */
  const referrer = useReferrerState()

  if (!seats || seats.limit === null) return null

  const { used, limit } = seats
  const seatWord = limit === 1 ? 'seat' : 'seats'

  // Name the actual numbers. "You have reached your seat limit" tells someone who is
  // already looking at a list of their colleagues nothing they did not know.
  //
  // Two sentences rather than one em-dash-joined clause (§15). The over-limit case gets
  // its own wording because "5 of 5 are in use" would be a plain lie at 7 of 5, and a
  // grandfathered company IS the case most likely to meet this modal first.
  const situation =
    used > limit
      ? `Your plan includes ${limit} ${seatWord} and you are currently using ${used}. ` +
        'Everyone already on your team keeps their access.'
      : `Your plan includes ${limit} ${seatWord} and all ${limit} are in use.`

  return (
    <Modal
      title="You have used every seat in your plan"
      open={open}
      onClose={onClose}
      actions={
        <Stack direction="row" spacing={3} alignItems="center">
          <Button variant="secondary" size="medium" onClick={onClose}>
            Close
          </Button>
          {/*
            §27/§46: the action is visible in a context the user can already see, so a
            missing permission DISABLES it with a tooltip naming what to ask for, rather
            than hiding it. Someone without billing access still needs to learn that the
            fix exists and who can perform it — a member who simply cannot invite and is
            told nothing has no next step at all.
          */}
          <PermissionGate
            permission="billing_change_plan"
            mode="disable"
            tooltip="You need the 'Change plan' permission. Ask an admin to upgrade."
          >
            <Button
              variant="primary"
              size="medium"
              onClick={() => {
                onClose()
                navigate(ROUTES.SETTINGS_COMPANY_BILLING_PLAN, { state: referrer })
              }}
            >
              {/* "Upgrade", not "See plans". The Users header button and this one
                  navigate to the SAME route, so two labels for one destination is
                  the product speaking with two voices about one thing. It matters
                  more now that Upgrade only appears in the header AT the limit —
                  both controls surface in the same moment, side by side. */}
              Upgrade
            </Button>
          </PermissionGate>
        </Stack>
      }
    >
      <Stack spacing={3}>
        {/* `warning`, not `info` — the copy is already written at warning severity.
            The over-limit sentence ends "Everyone already on your team keeps their
            access", which is REASSURANCE, and reassurance inside an info alert is
            incoherent: you do not reassure someone about neutral information. The
            sentence is only there because the situation reads alarming, and the tint
            should agree with the words rather than contradict them.

            Still NOT destructive/red. §43's test for the loud treatment is whether
            unrecoverable harm accrues, and that governs destructive CONFIRMATIONS —
            an action about to happen. This is the opposite: an action that did NOT
            happen, with nothing lost and nothing at risk. */}
        <Alert severity="warning" description={situation} />
        <Typography variant="body3" sx={{ lineHeight: 1.5 }}>
          A larger plan adds seats so you can invite more of your team. If you would rather
          stay on this plan, remove or archive someone from the Users list to free a seat.
        </Typography>
      </Stack>
    </Modal>
  )
}
